// ** React Imports
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useDispatch } from "react-redux";
import {
	mergeBulkMediaInfoAndComments,
	mergeBulkUserInfoAndMedia,
} from "store/modules/modal";

// ** Third Party Components
import { SwiperSlide } from "swiperNew/react";
import { Virtual, EffectCoverflow, Zoom, Navigation } from "swiperNew/modules";

// ** Custom Components
import CarouselSwiperSlideNew from "./CarouselSwiperSlideNew";
import Text from "components/typography/Text";

// ** Utils & Helper
import generatePath from "utils/generatePath";
import NavigationArrow from "./NavigationArrow";
import convertTextInModal from "utils/convertTextInModal";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import { StyledCarouselSwiperNew, StyledSwiperGradient } from "./styles";

// Import Swiper styles
import "swiperNew/css";
import "swiperNew/css/effect-coverflow";
import "../Swipers/styles/navigation.css";

// #####################################################

const CarouselSwiperNew = ({
	carouselSwiper,
	setCarouselSwiper,
	thumbnailSwiper,
	setShowComments,
	setActiveIndex,
	firstFilledSlideIndex,
	virtualSlides,
	mediaMeta,
	parsedLink,
	showSwipers,
	activeSlideData,
	setActiveSlideData,
	contentInfo,
	contentIdUrl,
	contentInfoLoading,
	showCommentsSection,
	setShowCommentsSection,
	setVisibleEditor,
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();

	const { isPhone, isTablet, isDesktop } = useContext(deviceTypeContext);
	const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
	const [isZoomedOut, setIsZoomedOut] = useState(false);

	const clickTimeoutRef = useRef(null);

	const { userLogin, contentId, mediaType, albumId } = parsedLink;
	const { left_hash, right_hash, right_amount } = mediaMeta || {};

	// ** Hook useMemo - Lista Slide'ów do wyrenderowania
	const slidesToRender = useMemo(() => {
		return virtualSlides.map((item, index) => (
			<SwiperSlide
				key={`carousel-slide-${item?.id}-${index}`}
				virtualIndex={index}
			>
				{item && item._filled && (
					<CarouselSwiperSlideNew
						item={item}
						itemIdFromUrl={contentId}
						contentInfo={contentInfo}
						contentIdUrl={contentIdUrl}
						carouselSwiper={carouselSwiper}
						isZoomedOut={isZoomedOut}
					/>
				)}
			</SwiperSlide>
		));
	}, [
		virtualSlides,
		contentId,
		contentInfo,
		carouselSwiper?.width,
		carouselSwiper?.height,
		isZoomedOut,
	]);

	const handleClick = () => {
		if (clickTimeoutRef.current) {
			clearTimeout(clickTimeoutRef.current);
			clickTimeoutRef.current = null;
		} else {
			clickTimeoutRef.current = setTimeout(() => {
				setIsDescriptionVisible(!isDescriptionVisible);
				clickTimeoutRef.current = null;
			}, 100);
		}
	};

	// ** Efekt pobierjacy listę komentarzy oraz informacje na temat zdjęcia
	useEffect(() => {
		const controller = new AbortController();
		const abortSignal = controller.signal;
		if (location.hash && activeSlideData) {
			dispatch(
				mergeBulkMediaInfoAndComments({
					userLogin,
					elementId: activeSlideData?.id,
					type: activeSlideData?.media_type,
					signal: abortSignal,
					onlyContentInfo: isPhone || isTablet,
				})
			).then(() => {
				setShowComments(true);
			});
		}

		return () => {
			controller.abort();
		};
	}, [activeSlideData?.id, isPhone, isTablet]);

	const handleZoomChange = (swiper, scale) => {
		if (scale > 1.5 && showCommentsSection && (isPhone || isTablet)) {
			setVisibleEditor({
				type: null,
				id: null,
			});
			setShowCommentsSection(false);
		}

		if (scale < 1 && !isZoomedOut) {
			setIsZoomedOut(true);
		} else if (scale > 1 && isZoomedOut) {
			setIsZoomedOut(false);
		}
	};
	// #####################################################

	return (
		<StyledCarouselSwiperNew
			onZoomChange={handleZoomChange}
			className="carouselSwiper noselect"
			style={{ visibility: showSwipers ? "visible" : "hidden" }}
			modules={[EffectCoverflow, Virtual, Zoom, Navigation]}
			zoom
			navigation
			effect={"coverflow"}
			noSwiping
			noSwipingClass="swiper-no-swiping"
			slidesPerView={1}
			threshold={10}
			spaceBetween={20}
			touchAngle={60}
			touchRatio={isDesktop ? 0.8 : 1}
			virtual={{
				enabled: true,
				cache: true,
				addSlidesAfter: 0,
				addSlidesBefore: 0,
			}}
			coverflowEffect={{
				rotate: 5,
				scale: 0.85,
				stretch: 0,
				depth: 10,
				modifier: 1,
				slideShadows: true,
			}}
			preventClicks
			preventClicksPropagation
			onClick={handleClick}
			// ** (1) Przypisanie slidera do zmiennej 'carouselSwiper'
			onSwiper={(swiper) => {
				setCarouselSwiper(swiper);
			}}
			// ** (2) Event wywoływany po zmianie slajdu:
			// ** - ustawienie indexu aktywnego slajdu do zmiennej stanu komponentu,
			// ** - zapisanie danych aktywnego slajdu do zmiennej stanu komponentu,
			// ** - przesunięcie 'thumbnailSwiper'a' do odpowiedniego indexu,
			onSlideChange={(swiper) => {
				const activeSlideDataTemp = virtualSlides[swiper.activeIndex];
				setActiveIndex(swiper?.activeIndex);
				setActiveSlideData(activeSlideDataTemp);
				thumbnailSwiper?.slideTo(swiper?.activeIndex);
				setShowCommentsSection(false);
			}}
			// ** (3) Event wywoływany po rozpoczęciu przejścia do innego slajdu
			onTransitionStart={(swiper) => {
				if (
					swiper?.zoom?.scale > 1 &&
					typeof swiper?.zoom?.out === "function"
				) {
					swiper.zoom.out();
				}
				if (virtualSlides?.length > 1) {
					isDesktop && setShowComments(false);
				}
			}}
			// ** (4) Event wywoływany po zakończeniu przejścia do innego slajdu
			onTransitionEnd={() => {
				if (
					userLogin &&
					activeSlideData?.media_type &&
					activeSlideData?.id
				) {
					const newHash = generatePath(
						`users/:login/:mediaType/:mediaId/:albumId?`,
						{
							login: userLogin,
							mediaType: activeSlideData?.media_type,
							mediaId: activeSlideData?.id,
							albumId,
						}
					);

					if (newHash !== location.hash.slice(1)) {
						history.replace({
							hash: newHash,
							search: location.search,
						});
					}
				}
			}}
			// ** (5) Event wywoływany po zakończeniu animacji przejścia do POPRZEDNIEGO slajdu
			onSlidePrevTransitionEnd={() => {
				if (
					virtualSlides[firstFilledSlideIndex] &&
					firstFilledSlideIndex !== 0 &&
					left_hash
				) {
					dispatch(
						mergeBulkUserInfoAndMedia({
							userLogin,
							elementId: virtualSlides[firstFilledSlideIndex].id,
							type: mediaType,
							vector: "left",
							album: albumId,
							leftHash: left_hash,
						})
					);
				}
				setShowComments(true);
			}}
			// ** (5) Event wywoływany po zakończeniu animacji przejścia do NASTĘPNEGO slajdu
			onSlideNextTransitionEnd={() => {
				if (
					virtualSlides &&
					carouselSwiper &&
					virtualSlides.length - carouselSwiper.activeIndex <= 10 &&
					right_amount > 0 &&
					right_hash
				) {
					dispatch(
						mergeBulkUserInfoAndMedia({
							userLogin,
							elementId: virtualSlides[firstFilledSlideIndex].id,
							type: mediaType,
							vector: "right",
							album: albumId,
							rightHash: right_hash,
						})
					);
				}
				setShowComments(true);
			}}
			// ** Event wywoływany po rozpoczęciu zakończeniu przeciagania slajdu przed przejściem do następnego
			onSlideResetTransitionEnd={() => {
				if (userLogin && activeSlideData && activeSlideData.id) {
					setShowComments(true);
				}
			}}
			onResize={(swiper) => {
				// ** Aktualizacja swipera po zmianie rozmiaru okna / rotacji ekranu
				swiper.update();

				setTimeout(() => {
					setShowComments(true);
				}, 1000);
			}}
			onSliderFirstMove={() => {
				if (virtualSlides?.length > 1) {
					setShowComments(false);
				}
			}}
		>
			{slidesToRender}

			{(isPhone || isTablet) &&
				isDescriptionVisible &&
				!contentInfoLoading &&
				!contentInfo.data?.videoUrl &&
				contentInfo?.data?.descriptionRaw &&
				contentInfo.data.descriptionRaw.length > 0 && (
					<StyledSwiperGradient>
						<Text color="whiteAlways" textBreak>
							{convertTextInModal({
								pieces: contentInfo?.data?.description,
							})}
						</Text>
					</StyledSwiperGradient>
				)}
			{!isPhone && !isTablet && virtualSlides?.length > 1 && (
				<>
					<NavigationArrow direction="prev" swiper={carouselSwiper} />
					<NavigationArrow direction="next" swiper={carouselSwiper} />
				</>
			)}
		</StyledCarouselSwiperNew>
	);
};

export default CarouselSwiperNew;
